import { Box, DialogContent, SvgIcon, Typography } from '@mui/material'
import { differenceInMinutes, differenceInSeconds, format } from 'date-fns'
import PropTypes from 'prop-types'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import AvatarComponent from '@hapana/shared/web/components/shared/avatar/avatar.component'
import { AVATAR_SIZE } from '@hapana/shared/web/components/shared/avatar/avatar.constants'
import BannerAlert from '@hapana/shared/web/components/shared/banner-alert/banner-alert.component'
import { SEVERITY } from '@hapana/shared/web/components/shared/banner-alert/banner-alert.constants'
import Chip from '@hapana/shared/web/components/shared/chip/chip.component'
import { CHIP_SIZE } from '@hapana/shared/web/components/shared/chip/chip.constants'
import DialogLayout from '@hapana/shared/web/components/shared/dialog/dialog-layout/dialog-layout.component'
import {
  CLOSE_TYPES,
  SIZES,
} from '@hapana/shared/web/components/shared/dialog/dialog-layout/dialog-layout.constants'
import { useDialog } from '@hapana/shared/web/components/shared/dialog/dialog.hooks'
import Loading from '@hapana/shared/web/components/shared/loading/loading.component'
import Tooltip from '@hapana/shared/web/components/shared/tooltip/tooltip.component'
import useSiteStore from '_stores/site.store'
import { calculateAge } from '@hapana/shared/utils/date.util'
import { formatOrdinal } from '@hapana/shared/utils/string.util'
import AssignEquipmentErrorAlert from '_components/check-in-widget/modal-check-in/modal-check-in-tabs/manage-equipments-tab/alert-dialogs/assign/assign-equipment-error.alert.component'
import AssignEquipmentQueueSuccessAlert from '_components/check-in-widget/modal-check-in/modal-check-in-tabs/manage-equipments-tab/alert-dialogs/assign/assign-equipment-queue-success.alert.component'
import { StartTimerDialog } from '_components/check-in-widget/modal-check-in/modal-check-in-tabs/waitline-tab/start-timer-dialog/start-timer-dialog.component'
import { EQUIPMENT_QUALITY_CATEGORIES_MAP, EQUIPMENT_STATUSES } from '_constants/equipments'
import { useGetClient, useGetClientAssignments } from '_hooks/clients.hooks'
import { useAddClientToEquipmentQueue } from '_hooks/equipment-queue.hooks'
import { useListEquipments, useTanningEligibility } from '_hooks/equipments.hooks'

import { EquipmentImage } from '_components/check-in-widget/equipment-image/equipment-image.component'
import { BADGE_LABEL_MAP, BORDER_COLOR_BY_STATUS } from './assign-to-equipment-dialog.constants'
import { styles, handleStylesWithProps } from './assign-to-equipment-dialog.styles'

export const AssignToEquipmentDialog = ({ clientId, clientName }) => {
  const { t } = useTranslation(['checkIn'])
  const { closeDialog, pushDialog, popDialog } = useDialog()
  const siteId = useSiteStore((state) => state.currentSite?.siteId)
  const { data, isFetching: isLoadingEquipments } = useListEquipments(siteId, { last: true })
  const [selectedEquipmentId, setSelectedEquipmentId] = useState(null)
  const { data: client, isLoading: isLoadingClient } = useGetClient(clientId, siteId)
  const { data: clientAssignments } = useGetClientAssignments(clientId, siteId, true, undefined)
  const { mutate: addToQueue, isLoading: isAddingToQueue } = useAddClientToEquipmentQueue()

  const equipments = useMemo(() => data?.equipments || [], [data])

  const selectedEquipment = useMemo(
    () => equipments.find((eq) => eq.equipmentId === selectedEquipmentId),
    [equipments, selectedEquipmentId]
  )

  const {
    data: eligibilityData,
    isLoading: isLoadingEligibility,
    error: eligibilityError,
  } = useTanningEligibility(selectedEquipment?.category, clientId, siteId, {
    enabled: !!selectedEquipment?.category && !!clientId && !!siteId,
  })

  if (isLoadingEquipments || isLoadingClient || !client) {
    return (
      <DialogLayout
        closeType={CLOSE_TYPES.ICON}
        closeAction={closeDialog}
        title={t('checkIn:equipments.assignToEquipment.title')}
        size={SIZES.SM}
        hasBackLabel
        primaryAction={{
          label: t('checkIn:equipments.assignToEquipment.startTimer'),
          props: { disabled: true },
          action: () => {},
        }}
      >
        <DialogContent sx={styles.dialogLoadingContainer}>
          <Loading />
        </DialogContent>
      </DialogLayout>
    )
  }

  const hasQueue = selectedEquipment?.status !== EQUIPMENT_STATUSES.AVAILABLE
  const isAvailable = selectedEquipment
    ? selectedEquipment?.status === EQUIPMENT_STATUSES.AVAILABLE
    : false

  const isEligible = eligibilityData?.eligible ?? false

  const formatTimeUntilAllowed = (minutes) => {
    if (!minutes || minutes <= 0) return null

    const hours = Math.floor(minutes / 60)
    const remainingMinutes = minutes % 60

    if (hours > 0 && remainingMinutes > 0) {
      return t('checkIn:equipments.eligibility.timeFormat.hoursAndMinutes', {
        hours,
        minutes: remainingMinutes,
      })
    } else if (hours > 0) {
      return t('checkIn:equipments.eligibility.timeFormat.hoursOnly', { hours })
    } else {
      return t('checkIn:equipments.eligibility.timeFormat.minutesOnly', {
        minutes: remainingMinutes,
      })
    }
  }

  const getEligibilityErrorMessage = () => {
    const name = client?.firstName || clientName

    if (eligibilityError || !eligibilityData) {
      return t('checkIn:equipments.eligibility.notEligibleMessage', {
        clientName: name,
      })
    }

    if (eligibilityData.eligible) return null

    // Check if this is a time-based violation
    const hasTimeViolation = eligibilityData.minutesUntilAllowed > 0
    const isAgeViolation =
      eligibilityData.uvAge?.minimumViolated ||
      eligibilityData.uvAge?.parentalConsentRequired ||
      eligibilityData.nonUvAge?.minimumViolated ||
      eligibilityData.nonUvAge?.parentalConsentRequired

    // If it's time-related and not an age violation, show combined message with time
    if (hasTimeViolation && !isAgeViolation) {
      const timeUntilAllowed = formatTimeUntilAllowed(eligibilityData.minutesUntilAllowed)
      if (timeUntilAllowed) {
        return t('checkIn:equipments.eligibility.notEligibleWithTimeMessage', {
          clientName: name,
          timeUntilAllowed,
        })
      }
    }

    // Default message for age violations or when time format fails
    return t('checkIn:equipments.eligibility.notEligibleMessage', {
      clientName: name,
    })
  }

  const lastTanning = clientAssignments?.[0]
  const lastTanningDate = lastTanning?.doneAt || lastTanning?.assignedAt || lastTanning?.createdAt
  // GraphQL String scalar serializes Date objects as ms timestamps (e.g. "1745280000000")
  const parsedLastTanningDate = lastTanningDate
    ? new Date(Number.isFinite(+lastTanningDate) ? +lastTanningDate : lastTanningDate)
    : null

  const handlePrimaryAction = () => {
    if (!selectedEquipmentId) return
    if (isAvailable) {
      pushDialog(StartTimerDialog, {
        clientId,
        clientName,
        equipmentId: selectedEquipmentId,
        equipment: selectedEquipment,
      })
      return
    }

    addToQueue(
      { clientId, equipmentId: selectedEquipmentId },
      {
        onSuccess: () => {
          pushDialog(AssignEquipmentQueueSuccessAlert, { clientName })
        },
        onError: (error) => {
          pushDialog(AssignEquipmentErrorAlert, {
            clientName,
            errorMessage: error?.response?.errors?.[0]?.message,
          })
        },
      }
    )
  }

  const renderEligibilityAlert = () => {
    if (!selectedEquipment) return null

    if (isLoadingEligibility) {
      return <Loading />
    }

    if (!isEligible) {
      return (
        <BannerAlert
          severity={SEVERITY.ERROR}
          hasBackground
          message={getEligibilityErrorMessage()}
          isMessageBold={false}
          descriptionHasDotDivider={false}
        />
      )
    }

    if (hasQueue && selectedEquipment) {
      return (
        <BannerAlert
          severity={SEVERITY.WARNING}
          hasBackground
          message={t('checkIn:equipments.assignToEquipment.queueWarningTitle', {
            count: selectedEquipment.queue.length,
            equipmentName: selectedEquipment.name,
            clientName,
            positionOrdinal: formatOrdinal(selectedEquipment.queue.length + 1),
          })}
          isMessageBold={false}
          descriptionHasDotDivider={false}
        />
      )
    }

    return (
      <BannerAlert
        severity={SEVERITY.SUCCESS}
        hasBackground
        message={t('checkIn:equipments.assignToEquipment.noQueueTitle', {
          equipmentName: selectedEquipment.name,
        })}
        isMessageBold={false}
        descriptionHasDotDivider={false}
      />
    )
  }

  return (
    <DialogLayout
      closeType={CLOSE_TYPES.ICON}
      closeAction={closeDialog}
      title={t('checkIn:equipments.assignToEquipment.title')}
      size={SIZES.SM}
      hasBackLabel
      primaryAction={{
        label: isAvailable
          ? t('checkIn:equipments.assignToEquipment.startTimer')
          : t('checkIn:equipments.assignToEquipment.addToQueue'),
        props: { disabled: !selectedEquipmentId || isLoadingEligibility },
        isLoading: !isAvailable && isAddingToQueue,
        action: handlePrimaryAction,
      }}
    >
      <DialogContent sx={styles.dialogContent}>
        <Box sx={styles.container}>
          <Typography typography="header2" color="gray.80" sx={styles.title}>
            {t('checkIn:equipments.assignToEquipment.title')}
          </Typography>

          <Box sx={styles.personCard}>
            <AvatarComponent
              item={{
                src: client?.profileImage?.url || 'https://via.placeholder.com/80',
                alt: t('checkIn:equipments.assignToEquipment.clientPhoto'),
              }}
              size={AVATAR_SIZE.SEARCH_RESULT}
            />
            <Box sx={styles.personInfo}>
              <Typography typography="header3" color="gray.100">
                {client?.firstName ? `${client.firstName} ${client.lastName || ''}` : clientName}
              </Typography>
              {client?.dateOfBirth && (
                <Typography typography="medium" color="gray.64">
                  {t('checkIn:equipments.assignToEquipment.yearsOld', {
                    age: calculateAge(client.dateOfBirth),
                  })}
                </Typography>
              )}
              {lastTanningDate && (
                <Typography typography="small" color="gray.64">
                  {t('checkIn:equipments.assignToEquipment.lastTanningSession', {
                    date: format(parsedLastTanningDate, 'MMM dd, yyyy'),
                    time: format(parsedLastTanningDate, 'p'),
                  })}
                </Typography>
              )}
            </Box>
          </Box>

          <Box sx={styles.outerWrapper}>
            <Box sx={styles.legendRow}>
              <Box>
                <Chip
                  label={t('checkIn:equipments.assignToEquipment.selected')}
                  size={CHIP_SIZE.MEDIUM}
                  sx={styles.selectedChip}
                />
              </Box>
              <Box>
                <Chip
                  label={t('checkIn:equipments.assignToEquipment.available')}
                  size={CHIP_SIZE.MEDIUM}
                  sx={styles.availableChip}
                />
              </Box>
              <Box>
                <Chip
                  label={t('checkIn:equipments.assignToEquipment.unavailable')}
                  size={CHIP_SIZE.MEDIUM}
                  sx={styles.unavailableChip}
                />
              </Box>
              <Box>
                <Chip
                  label={t('checkIn:equipments.assignToEquipment.dirty')}
                  size={CHIP_SIZE.MEDIUM}
                  sx={styles.dirtyChip}
                />
              </Box>
              <Box>
                <Chip
                  label={t('checkIn:equipments.assignToEquipment.inUse')}
                  size={CHIP_SIZE.MEDIUM}
                  sx={styles.inUseChip}
                />
              </Box>
            </Box>

            <Box sx={styles.mapWrapper}>
              <Box sx={styles.mapContent}>
                <Box sx={styles.mapGrid}>
                  {equipments.map((eq) => {
                    const { status } = eq
                    const isSelected = selectedEquipmentId === eq.equipmentId
                    const borderColor = isSelected
                      ? BORDER_COLOR_BY_STATUS[EQUIPMENT_STATUSES.SELECTED]
                      : BORDER_COLOR_BY_STATUS[status]

                    const assignments = eq.assignments || []
                    const latestAssignment = assignments[0]
                    const now = new Date()
                    const timerMinutes = latestAssignment?.timerMinutes || 0
                    const delayMinutes = latestAssignment?.delayMinutes || 0
                    const createdAt = latestAssignment?.createdAt
                      ? new Date(+latestAssignment.createdAt)
                      : null
                    const endAt = createdAt
                      ? new Date(createdAt.getTime() + (timerMinutes + delayMinutes) * 60000)
                      : null
                    const minutesLeft = endAt ? Math.max(0, differenceInMinutes(endAt, now)) : null
                    const secondsLeft = endAt ? Math.max(0, differenceInSeconds(endAt, now)) : null

                    const showTooltip =
                      (status === EQUIPMENT_STATUSES.IN_USE ||
                        status === EQUIPMENT_STATUSES.DELAY) &&
                      latestAssignment &&
                      minutesLeft != null
                    const tooltipTitle = showTooltip
                      ? secondsLeft === 0
                        ? // Timer elapsed but the equipment never reported it finished —
                          // its status is still in use, so reflect that instead of "Finished"
                          t('checkIn:equipments.assignToEquipment.inUse')
                        : minutesLeft === 0 && secondsLeft != null
                          ? t('checkIn:equipments.manageEquipments.timerTimeLeftSeconds', {
                              seconds: secondsLeft,
                            })
                          : t('checkIn:equipments.manageEquipments.timerTimeLeft', {
                              time: minutesLeft,
                            })
                      : ''

                    const card = (
                      <Box
                        sx={[styles.cardWrapper, { borderColor }]}
                        onClick={() => setSelectedEquipmentId(eq.equipmentId)}
                      >
                        <Box sx={styles.imageContainer}>
                          <EquipmentImage
                            src={eq.image?.medium || eq.image?.thumbnail || eq.image?.db}
                            name={eq.name}
                            sx={styles.equipmentImage}
                            loading="lazy"
                            referrerPolicy="no-referrer"
                          />
                        </Box>
                        {EQUIPMENT_QUALITY_CATEGORIES_MAP[eq.qualityCategory] && (
                          <Box sx={styles.medalWrapper}>
                            <SvgIcon
                              component={EQUIPMENT_QUALITY_CATEGORIES_MAP[eq.qualityCategory].icon}
                              inheritViewBox
                              sx={handleStylesWithProps(eq.qualityCategory).equipmentMedalIcon}
                            />
                          </Box>
                        )}
                      </Box>
                    )

                    return showTooltip ? (
                      <Tooltip
                        key={eq.equipmentId}
                        title={tooltipTitle}
                        placement="top"
                        disablePortal
                      >
                        {card}
                      </Tooltip>
                    ) : (
                      <Tooltip
                        key={eq.equipmentId}
                        title={`${eq.name} -  ${t('checkIn:equipments.assignToEquipment.room', {
                          roomId: eq.timerId,
                        })}`}
                        placement="top"
                        disablePortal
                      >
                        {card}
                      </Tooltip>
                    )
                  })}
                </Box>
                <Box sx={styles.medalsContainer}>
                  {Object.values(EQUIPMENT_QUALITY_CATEGORIES_MAP).map((category) => (
                    <Box key={category.value} sx={styles.medalContainer}>
                      <SvgIcon component={category.icon} inheritViewBox sx={styles.medalIcon} />
                      <Typography typography="header5" color="gray.40">
                        {t(category.label)}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
            </Box>
          </Box>

          <Box sx={styles.queueAlert}>{renderEligibilityAlert()}</Box>
        </Box>
      </DialogContent>
    </DialogLayout>
  )
}

export default AssignToEquipmentDialog

AssignToEquipmentDialog.propTypes = {
  clientId: PropTypes.string,
  clientName: PropTypes.string,
}
